import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { storyblokEditable } from '@storyblok/react';
import { Link } from 'react-router-dom';

export type CmsLinkBlock = {
  _uid: string;
  _editable?: string;
  component: 'link';
  href?: string;
  link?: { url: string; linktype: 'url' | 'story'; cached_url: string };
  children?: string;
};

export type CmsNavigationBlock = {
  _uid: string;
  _editable?: string;
  component: 'navigation';
  children?: CmsLinkBlock[];
};

export const isCmsNavigationBlock = (value: unknown): value is CmsNavigationBlock =>
  typeof value === 'object' &&
  value !== null &&
  'component' in value &&
  value.component === 'navigation';

const getLinkHref = (block: CmsLinkBlock): string =>
  block.href ?? block.link?.cached_url ?? block.link?.url ?? '#';

const isExternalLink = (block: CmsLinkBlock): boolean => {
  const href = getLinkHref(block);
  return href.startsWith('http') || block.link?.linktype === 'url';
};

type CmsNavigationLinksProps = {
  block: CmsNavigationBlock;
  linkClassName: string;
  canAccessLink?: (href: string) => boolean;
};

export const CmsNavigationLinks = ({
  block,
  linkClassName,
  canAccessLink = () => true,
}: CmsNavigationLinksProps) => (
  <nav {...storyblokEditable(block)}>
    {(block.children ?? []).map((link) => {
      const href = getLinkHref(link);
      if (!canAccessLink(href)) {
        return null;
      }
      return (
        <DropdownMenu.Item key={link._uid} asChild>
          {isExternalLink(link) ? (
            <a
              {...storyblokEditable(link)}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
            >
              {link.children ?? href}
            </a>
          ) : (
            <Link {...storyblokEditable(link)} to={href} className={linkClassName}>
              {link.children ?? href}
            </Link>
          )}
        </DropdownMenu.Item>
      );
    })}
  </nav>
);
